export type OrientationReading = {
  absolute?: boolean; alpha: number | null; beta: number | null; gamma: number | null;
  webkitCompassHeading?: number; webkitCompassAccuracy?: number;
};
export const normalizeHeading = (degrees: number) => ((degrees % 360) + 360) % 360;
const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

/** Heading of the top of the displayed screen, clockwise from north. */
export function compassHeading(event: OrientationReading, screenAngle = 0): number | null {
  if (finite(event.webkitCompassHeading) && event.webkitCompassHeading >= 0 && event.webkitCompassHeading < 360) {
    // WebKit reports magnetic north; negative accuracy means uncalibrated.
    if (event.webkitCompassAccuracy !== undefined && (!finite(event.webkitCompassAccuracy) || event.webkitCompassAccuracy < 0 || event.webkitCompassAccuracy > 30)) return null;
    return normalizeHeading(event.webkitCompassHeading + screenAngle);
  }
  // Relative alpha has an arbitrary origin and cannot be used as a compass.
  if (!event.absolute || !finite(event.alpha) || !finite(event.beta) || !finite(event.gamma)) return null;
  const radians = Math.PI / 180;
  const a = event.alpha * radians, b = event.beta * radians, g = event.gamma * radians, s = screenAngle * radians;
  // Project the screen's top edge through Rz(alpha) Rx(beta) Ry(gamma).
  const east = (Math.cos(a)*Math.cos(g)-Math.sin(a)*Math.sin(b)*Math.sin(g))*Math.sin(s)-Math.sin(a)*Math.cos(b)*Math.cos(s);
  const north = (Math.sin(a)*Math.cos(g)+Math.cos(a)*Math.sin(b)*Math.sin(g))*Math.sin(s)+Math.cos(a)*Math.cos(b)*Math.cos(s);
  if (Math.hypot(east,north) < 0.15) return null; // Screen top points almost vertically.
  return normalizeHeading(Math.atan2(east,north)/radians);
}

export type HeadingSample = {degrees:number; timestamp:number; source:'compass'|'course'};
export function smoothHeading(previous:HeadingSample|null, degrees:number, now:number):HeadingSample {
  // Circular smoothing avoids the 359° -> 0° jump through south. Reset after a gap.
  const elapsed = previous ? now-previous.timestamp : Infinity;
  const delta = previous ? normalizeHeading(degrees-previous.degrees+180)-180 : 0;
  const weight = 1-Math.exp(-Math.max(0,elapsed)/120);
  return {degrees:previous&&elapsed<2000 ? normalizeHeading(previous.degrees+delta*weight):degrees,timestamp:now,source:'compass'};
}

export function travelHeading(position:GeolocationPosition|null, now:number):HeadingSample|null {
  if (!position || now-position.timestamp>10000 || position.timestamp>now+1000) return null;
  const {heading,speed,accuracy}=position.coords;
  // A course while stationary (or without measured speed) is often GPS drift.
  if (!finite(heading)||heading<0||heading>=360||!finite(speed)||speed<0.8||!finite(accuracy)||accuracy<0||accuracy>50) return null;
  return {degrees:heading,timestamp:position.timestamp,source:'course'};
}

export function currentHeading(compass:HeadingSample|null, course:HeadingSample|null, now:number):HeadingSample|null {
  // Orientation events may stop when the phone is still. The listener clears this
  // sample on suspension, screen rotation, or sustained unusable sensor readings.
  if(compass)return compass;
  if(course&&now-course.timestamp<5000)return course;
  return null;
}
